import Container, { Service } from 'typedi';
import { Utils } from '../utils';
import { ILogger } from '@sparkware/uc-sdk-core';
import { FeatureAvailabilityToken, WindowToken } from '../../injection-tokens';
import { LoggerProvider } from '../logger';
import OpenRegistrationApi from '../../../APIs/OpenRegistrationApi';
import {
    LocalSimpleStoreService,
    SessionSimpleStoreService,
} from '../storage/implementations/simple-store';
import { IFeatureAvailability } from '../feature/feature-availability/feature-availability-interface';
import { ClientProvider, Brand } from '../../models/enums/general-enums';
import { ICloseButtonVisibility } from './models';
import { INativeService } from '../native/models/INativeService';
import { LoaderManager } from '../../loaders/LoaderManager';

@Service()
export class RegistrationOldDataService {
    private readonly _utils: Utils;
    protected readonly _logger: ILogger;
    private readonly _sessionSimpleStoreService: SessionSimpleStoreService;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;
    private readonly _window: Window;
    private readonly _featureAvailability: IFeatureAvailability;

    // Constants for brandIds that don't show close button on PC
    private readonly _brandsWithoutCloseButtonPC = [Brand.Sport_NL, Brand.Sport_UK];

    // Constants for brandIds that show close button on mobile
    private readonly _brandsWithCloseButtonMobile = [
        Brand.Sport_US_CO,
        Brand.Sport_CA_ON,
        Brand.Sport_US_VA,
        Brand.Sport_US_IN,
        Brand.Sport_US_MI,
    ];

    private get _nativeServicePromise(): Promise<INativeService> {
        return LoaderManager.Instance.NativeServiceLoader.Instance;
    }

    constructor() {
        this._utils = Container.get(Utils);
        this._logger = Container.get(LoggerProvider).getLogger('RegistrationOldDataService');
        this._sessionSimpleStoreService = Container.get(SessionSimpleStoreService);
        this._window = Container.get(WindowToken);
        this._featureAvailability = Container.get(FeatureAvailabilityToken);
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
    }

    public async getRegistrationData(): Promise<any> {
        try {
            const { response, errorResponse } = await OpenRegistrationApi.GetOpenRegistration();
            if (errorResponse || !response) {
                this._logger.warn(
                    'getRegistrationData: Error while getting registration data',
                    errorResponse,
                );
                return null;
            }
            const specificNavigation = JSON.parse(response.SpecificNavigation);
            const sCut = this._utils.GetSCutObject();
            if (sCut) {
                specificNavigation.TestData = sCut?.get('testdata');
            }
            await this.appendNativeParams(specificNavigation);
            response.SpecificNavigation = JSON.stringify(specificNavigation);
            return response;
        } catch (e) {
            this._logger.error('getRegistrationData: Error while getting registration data', e);
        }
    }

    public async buildMemberShipParams(
        data: any,
        dlParam: string | null,
        linkId: string | null,
        isOpenExternal: boolean,
    ): Promise<Record<string, any>> {
        const params: Record<string, string> = {
            // Default params
            SourceProductPackage: this._window.pageContextManager
                .getDeviceData()
                .productPackage?.toString(),
            ClientVersion: this._window.pageContextManager.getDeviceData().clientVersion,
            EncryptionType: data.EncryptionType,
            PasswordEncryptionType: data.PasswordEncryptionType,
            MachineID: data.MachineID,
            HardwareID: data.HardwareID,
            TargetPackageNavigationView: data.TargetPackageNavigationView,
            LangID: data.LangID,
            BrandID: data.BrandId,
            SourceSubBrand: data.SourceSubBrandID,
            platformID: data.PlatformID,
            TargetProductPackage: data.TargetProductPackage,
            TargetSubBrand: data.TargetSubBrand,
            UserInfo: null,
            IsRealMode: data.IsRealMode.toString(),
            PromCode: null,
            GoogleSessionID: this._utils.GetGALinkerParam(),
            BusinessCorrelationID: data.BusinessCorrelationID,
        };

        // Optional params
        if (dlParam?.length) {
            params.dl = dlParam;
        }

        if (linkId?.length) {
            params.LinkID = linkId;
        }

        const membershipMkw = this._sessionSimpleStoreService.get('membership_mkw');
        if (membershipMkw) {
            params.mkw = membershipMkw;
        }

        // Navigation params
        const specificNavigation = JSON.parse(data.SpecificNavigation);
        const origin = this._window.location.origin;
        const callbackUrl = `${origin}${this.getMembershipCallbackQSParameters(data)}`;
        const callbackDomain = encodeURIComponent(callbackUrl);

        specificNavigation.CallbackDomain = callbackDomain;

        if (isOpenExternal) {
            specificNavigation.callbackurl = callbackDomain;
        }

        const deviceData = this._window.pageContextManager.getDeviceData();
        if (deviceData.isMobile) {
            specificNavigation.EnableBackButton = 'True';
            const isNDBSEnable = this._featureAvailability.IsFeatureEnabled('ENABLE_GC_NDBS');
            if (isNDBSEnable) {
                specificNavigation.EnableNativeAppDownload = !isNDBSEnable;
            }
        }

        const localStorageRegistrationData = this._localSimpleStoreService.get('registration_data');
        let registrationData = {};

        if (localStorageRegistrationData) {
            try {
                registrationData = JSON.parse(localStorageRegistrationData);
            } catch (e) {
                this._logger.warn('Invalid registration data JSON', e);
            }
        }

        specificNavigation.StoreRegistrationCookiesData = registrationData;

        if (dlParam?.length) {
            specificNavigation.dl = dlParam;
        }

        params.SpecificNavigation = JSON.stringify(specificNavigation);

        const nativeService = await this._nativeServicePromise;
        if (nativeService) {
            const { AppID, IDFA, Bundle, OS, DeviceType } =
                await nativeService.getNativeStoreData();

            return {
                ...params,
                ...(Bundle?.length && { BundleID: Bundle }),
                ...(OS?.length && { OS }),
                ...(DeviceType?.length && { DeviceType }),
                ...(AppID?.length && { AppID }),
                ...(IDFA?.length && { DeviceID: IDFA }),
            };
        } else {
            return params;
        }
    }

    public getMembershipCallbackQSParameters(res: any, mobileLobbyUrl?: string): string {
        let callbackUrlQS = '';

        if (!this._utils.isNullOrEmpty(res.NativeDeviceType)) {
            callbackUrlQS += '&DeviceType=' + res.NativeDeviceType;
        }

        if (!this._utils.isNullOrEmpty(res.NativeOS)) {
            callbackUrlQS += '&OS=' + res.NativeOS;
        }

        if (!this._utils.isNullOrEmpty(res.NativeLanguage)) {
            callbackUrlQS += '&Language=' + res.NativeLanguage;
        }

        if (!this._utils.isNullOrEmpty(res.NativeClientVersion)) {
            callbackUrlQS += '&NativeClientVersion=' + res.NativeClientVersion;
        }

        if (!this._utils.isNullOrEmpty(res.NativeAppName)) {
            callbackUrlQS += '&AppName=' + res.NativeAppName;
        }

        if (!this._utils.isNullOrEmpty(res.BrandId)) {
            callbackUrlQS += '&BrandID=' + res.BrandId;
        }

        if (!this._utils.isNullOrEmpty(res.LangID)) {
            callbackUrlQS += '&Lang=' + res.LangID;
        }

        if (!this._utils.isNullOrEmpty(callbackUrlQS)) {
            callbackUrlQS = '?' + callbackUrlQS.substring(1);
        }

        return callbackUrlQS;
    }

    public hasCloseButton(): ICloseButtonVisibility {
        try {
            const { brandId } = this._window.pageContextManager.getBrandData();
            const { country } = this._window.pageContextManager.getGlobalizationData();
            const { name } = this._window.pageContextManager.getClientProviderData();
            const brandsWithoutCloseButtonPC = [...this._brandsWithoutCloseButtonPC];

            // Handle UK specific logic
            if (country.toLowerCase() === 'gbr') {
                // TODO: Remove this line when SCV is released on PROD
                brandsWithoutCloseButtonPC.push(Brand.Sport_Com);

                if (name === ClientProvider.Poker) {
                    brandsWithoutCloseButtonPC.push(Brand.Sport_Spoker);
                }
            }

            return {
                pc: !brandsWithoutCloseButtonPC.some((brand) => brand === brandId),
                mobile: this._brandsWithCloseButtonMobile.some((brand) => brand === brandId),
            };
        } catch (error) {
            this._logger.error('hasCloseButton: Error determining close button visibility', error);
            return {
                pc: false,
                mobile: false,
            };
        }
    }

    private async appendNativeParams(specificNavigation: Record<string, any>): Promise<void> {
        try {
            const nativeService = await this._nativeServicePromise;
            if (!nativeService) return;
            const { FirebaseAppID, FirebaseAppInstanceID, AppsFlyerID, AppsFlyerInstallData } =
                nativeService.getNativeStoreData();

            // Handle Firebase data
            const hasFirebaseData = FirebaseAppID || FirebaseAppInstanceID;
            if (hasFirebaseData) {
                specificNavigation.TestData = specificNavigation.TestData
                    ? JSON.parse(specificNavigation.TestData)
                    : {};

                if (FirebaseAppID) {
                    specificNavigation.TestData.FirebaseAppID = FirebaseAppID;
                }
                if (FirebaseAppInstanceID) {
                    specificNavigation.TestData.FirebaseAppInstanceID = FirebaseAppInstanceID;
                }
            }

            // Handle AppsFlyer data
            if (AppsFlyerID) {
                specificNavigation.AppsFlyerID = AppsFlyerID;
            }

            if (AppsFlyerInstallData) {
                specificNavigation.AFInstall = AppsFlyerInstallData;
            }

            const { iOSDefaultSerial, androidDefaultSerial } =
                this._window.pageContextManager.getNativeData();
            const { isIOS } = this._window.pageContextManager.getDeviceData();
            specificNavigation.defaultsr = isIOS ? iOSDefaultSerial : androidDefaultSerial;

            if (specificNavigation.defaultsr) {
                delete specificNavigation.Serial;
            }

            const { mitIdSwitchBackUrl } = this._window.pageContextManager.getSiteData();
            if (mitIdSwitchBackUrl) {
                specificNavigation.switchbackurl = mitIdSwitchBackUrl;
                specificNavigation.switchbackos = this._window.pageContextManager
                    .getDeviceData()
                    .osName.toLowerCase();
            }
        } catch (error) {
            this._logger.error('appendNativeParams: Error processing native data', error);
        }
    }
}
